import type { CoreNotifyEvent } from '@/services/websocket/types'
import { scheduleCoalescedServerEventListenerRun } from '@/services/server-events/coalesceServerEventListenerRun'

export type ServerEventPredicate = (event: CoreNotifyEvent, user?: CoreNotifyEvent) => boolean
export type ServerEventHandler = (event: CoreNotifyEvent) => void

interface ServerEventListener {
  predicate: ServerEventPredicate
  handler: ServerEventHandler
  source?: string
  /** Platform sessionId формы при регистрации — listener только для событий этого core. */
  sessionId?: string | null
}

class ServerEventRegistry {
  private listeners = new Map<string, Set<ServerEventListener>>()

  register(
    formId: string,
    predicate: ServerEventPredicate,
    handler: ServerEventHandler,
    source?: string,
    sessionId?: string | null,
  ): void {
    if (!this.listeners.has(formId)) {
      this.listeners.set(formId, new Set())
    }
    this.listeners.get(formId)!.add({ predicate, handler, source, sessionId })
  }

  clearForm(formId: string): void {
    this.listeners.delete(formId)
  }

  handleEvent(event: CoreNotifyEvent): void {
    this.listeners.forEach((entries) => {
      entries.forEach((entry) => {
        try {
          const eventCoreId = String(event.coreId ?? '').trim()
          const listenerSessionId = String(entry.sessionId ?? '').trim()
          if (
            eventCoreId &&
            listenerSessionId &&
            eventCoreId !== listenerSessionId
          ) {
            return
          }
          if (entry.predicate(event)) {
            entry.handler(event)
          }
        } catch (error) {
          console.error('[ServerEvents] Error in server event listener:', error, { source: entry.source })
        }
      })
    })
  }
}

export const serverEventRegistry = new ServerEventRegistry()

const resolveEventType = (event: any): string => {
  if (!event) return ''
  if (typeof event.type === 'string') return event.type
  if (event.type && typeof event.type === 'object' && 'value' in event.type) {
    return String(event.type.value)
  }
  if (typeof event.get === 'function') {
    const byGet = event.get('event')
    if (byGet !== undefined && byGet !== null) {
      return String(byGet)
    }
  }
  const fromData = event.data?._event?.value ?? event._event?.value
  if (fromData !== undefined && fromData !== null) {
    return String(fromData)
  }
  return ''
}

const markRestriction = (fn: ServerEventPredicate): ServerEventPredicate => {
  ;(fn as any).__isEventRestriction = true
  return fn
}

const createProxyEvent = (event: CoreNotifyEvent) => {
  const eventData = event?.data as Record<string, any> | undefined
  const readField = (key: string) => {
    if (!eventData) return undefined
    const entry = eventData[key]
    if (entry && typeof entry === 'object' && 'value' in entry) {
      return entry.value
    }
    return entry
  }
  const hasNestedFields = (prefix: string) => {
    if (!eventData) return false
    const needle = `${prefix}.`
    return Object.keys(eventData).some((key) => key.startsWith(needle))
  }
  const createProxyRecord = (prefix: string) => {
    const record = {
      get: (key: string) => {
        const fullKey = `${prefix}.${key}`
        const value = readField(fullKey)
        if (value !== undefined) {
          return value
        }
        if (hasNestedFields(fullKey)) {
          return createProxyRecord(fullKey)
        }
        return undefined
      },
      getFieldAsString: (key: string, def?: string) => {
        const value = record.get(key)
        return value === undefined || value === null ? def : String(value)
      },
      getFieldAsLong: (key: string, def?: number) => {
        const value = record.get(key)
        const parsed = value === undefined || value === null ? NaN : Number(value)
        return Number.isNaN(parsed) ? def : parsed
      },
      getFieldAsBoolean: (key: string, def?: boolean) => {
        const value = record.get(key)
        return value === undefined || value === null ? def : Boolean(value)
      }
    }
    return record
  }

  const proxyEvent = {
    type: resolveEventType(event),
    data: eventData,
    get: (key: string) => {
      if (key === 'event') {
        return event?.type ?? readField('_event')
      }
      const value = readField(key)
      if (value !== undefined) {
        return value
      }
      if (hasNestedFields(key)) {
        return createProxyRecord(key)
      }
      return undefined
    },
    getFieldAsString: (key: string, def?: string) => {
      const value = proxyEvent.get(key)
      return value === undefined || value === null ? def : String(value)
    },
    getFieldAsLong: (key: string, def?: number) => {
      const value = proxyEvent.get(key)
      const parsed = value === undefined || value === null ? NaN : Number(value)
      return Number.isNaN(parsed) ? def : parsed
    },
    getFieldAsBoolean: (key: string, def?: boolean) => {
      const value = proxyEvent.get(key)
      return value === undefined || value === null ? def : Boolean(value)
    }
  }

  return proxyEvent
}

export const getEventFieldValue = (event: CoreNotifyEvent, dottedPath: string): unknown => {
  if (!dottedPath) return undefined
  const segments = dottedPath.split('.').filter((s) => s.length > 0)
  if (segments.length === 0) return undefined
  let cur: any = createProxyEvent(event)
  for (const seg of segments) {
    if (cur == null) return undefined
    if (typeof cur.get === 'function') {
      cur = cur.get(seg)
    } else {
      return undefined
    }
  }
  return cur
}

const valuesMatchEq = (actual: unknown, expected: unknown): boolean => {
  if (actual === expected) return true
  if (actual == null && expected == null) return true
  if (actual == null || expected == null) return false
  if (typeof actual === 'number' && typeof expected === 'number') {
    return actual === expected
  }
  const sa = String(actual)
  const sb = String(expected)
  if (sa === sb) return true
  const na = Number(actual)
  const nb = Number(expected)
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na === nb) return true
  return false
}

const valuePresentInList = (actual: unknown, values: unknown[]): boolean => {
  if (actual === undefined || actual === null) return false
  for (const expected of values) {
    if (valuesMatchEq(actual, expected)) return true
  }
  return false
}

export const eventRestrictions = {
  eventIn: (events: string[]) => {
    const normalized = events.map(eventName => String(eventName).toLowerCase())
    return markRestriction((event: CoreNotifyEvent, user?: CoreNotifyEvent) => {
      const resolvedEvent = user ?? event
      const eventType = resolveEventType(resolvedEvent).toLowerCase()
      return normalized.includes(eventType)
    })
  },

  eventEq: (expected: string) => {
    const want = String(expected).toLowerCase()
    return markRestriction((event: CoreNotifyEvent, user?: CoreNotifyEvent) => {
      const resolvedEvent = user ?? event
      return resolveEventType(resolvedEvent).toLowerCase() === want
    })
  },

  and: (restrictions: Array<ServerEventPredicate | undefined | null>) => {
    return markRestriction((event: CoreNotifyEvent, user?: CoreNotifyEvent) => {
      const resolvedEvent = user ?? event
      return restrictions.every((restriction) => {
        if (!restriction) return true
        return restriction(resolvedEvent, resolvedEvent)
      })
    })
  },

  eq: (fieldPath: string, expected: unknown) => {
    return markRestriction((event: CoreNotifyEvent, user?: CoreNotifyEvent) => {
      const resolvedEvent = user ?? event
      const actual = getEventFieldValue(resolvedEvent, fieldPath)
      return valuesMatchEq(actual, expected)
    })
  },

  present: (fieldPath: string, values: unknown[]) => {
    const list = Array.isArray(values) ? values : []
    return markRestriction((event: CoreNotifyEvent, user?: CoreNotifyEvent) => {
      const resolvedEvent = user ?? event
      const actual = getEventFieldValue(resolvedEvent, fieldPath)
      return valuePresentInList(actual, list)
    })
  },

  not: (restriction: ServerEventPredicate) => {
    return markRestriction((event: CoreNotifyEvent, user?: CoreNotifyEvent) => {
      const resolvedEvent = user ?? event
      return !restriction(resolvedEvent, resolvedEvent)
    })
  },
}

const normalizeRestriction = (restriction: any): ServerEventPredicate => {
  if (!restriction) {
    return () => true
  }
  if (typeof restriction === 'string') {
    const restrictionValue = String(restriction)
    if (restrictionValue.startsWith('serverEvent:')) {
      return (event: CoreNotifyEvent) => {
        const eventData = (event?.data || {}) as Record<string, any>
        return eventData?._listener?.value === restrictionValue
      }
    }
    return eventRestrictions.eventIn([restrictionValue])
  }
  if (Array.isArray(restriction) && restriction.every(item => typeof item === 'string')) {
    return eventRestrictions.eventIn(restriction)
  }
  if (typeof restriction === 'function') {
    return (event: CoreNotifyEvent) => {
      const proxyEvent = createProxyEvent(event)
      return restriction(proxyEvent, proxyEvent)
    }
  }
  if (Array.isArray(restriction)) {
    const normalized = restriction.map((entry) => normalizeRestriction(entry))
    return eventRestrictions.and(normalized)
  }
  if (typeof restriction === 'object' && typeof restriction.match === 'function') {
    return (event: CoreNotifyEvent) => {
      const proxyEvent = createProxyEvent(event)
      return restriction.match.call(restriction, proxyEvent, proxyEvent)
    }
  }
  return () => true
}

/**
 * Временно накладывает поля server-event в En.data на время выполнения listener.
 * После завершения (включая async) откатывает overlay к исходному состоянию.
 */
const withEventData = async (
  enInstance: any,
  eventData: Record<string, any> | undefined,
  run: () => void | Promise<void>,
): Promise<void> => {
  if (!enInstance || !eventData) {
    await Promise.resolve(run())
    return
  }

  const data = enInstance.getData?.()
  if (!data || typeof data._getAllFields !== 'function') {
    await Promise.resolve(run())
    return
  }

  const currentFields = data._getAllFields()
  const eventKeys = Object.keys(eventData)
  if (eventKeys.length === 0) {
    await Promise.resolve(run())
    return
  }

  const previousValues: Record<string, any> = {}
  for (const key of eventKeys) {
    if (key in currentFields) {
      previousValues[key] = currentFields[key]
    }
  }

  data.add(eventData)
  try {
    await Promise.resolve(run())
  } finally {
    for (const key of eventKeys) {
      if (key in previousValues) {
        data.add({ [key]: previousValues[key] })
      } else if (typeof data.removeField === 'function') {
        data.removeField(key)
      }
    }
  }
}

export interface CreateListenersApiOptions {
  onAfterListener?: (enInstance: any) => void
  /** Вызывается до listener (await pending action / chainCalled). */
  onBeforeListener?: () => void | Promise<void>
  /** sessionId формы при registerOnServerEvent — не вызывать listener на чужом core. */
  sessionId?: string | null
}

export const createListenersApi = (
  formId: string,
  getEn: () => any,
  options?: CreateListenersApiOptions,
) => {
  const onAfterListener = options?.onAfterListener
  const onBeforeListener = options?.onBeforeListener
  const registeredSessionId = options?.sessionId ?? null
  return {
    add: (...args: any[]) => {
      const [first, second, third] = args
      let actualRestriction = first
      let actualCallback = second
      if (typeof third === 'function') {
        const secondIsRestriction = Boolean((second as any)?.__isEventRestriction)
        const thirdIsRestriction = Boolean((third as any)?.__isEventRestriction)
        if (secondIsRestriction && !thirdIsRestriction) {
          actualRestriction = second
          actualCallback = third
        } else if (thirdIsRestriction && !secondIsRestriction) {
          actualRestriction = third
          actualCallback = second
        } else {
          actualRestriction = second
          actualCallback = third
        }
      } else if (typeof first === 'function' && typeof second === 'function') {
        const firstIsRestriction = Boolean((first as any)?.__isEventRestriction)
        const secondIsRestriction = Boolean((second as any)?.__isEventRestriction)
        if (firstIsRestriction && !secondIsRestriction) {
          actualRestriction = first
          actualCallback = second
        } else if (secondIsRestriction && !firstIsRestriction) {
          actualRestriction = second
          actualCallback = first
        }
      }
      if (typeof actualCallback !== 'function') {

        return
      }
      const predicate = normalizeRestriction(actualRestriction)
      const source = (() => {
        try {
          if (typeof actualRestriction === 'function') {
            return actualRestriction.toString()
          }
          if (Array.isArray(actualRestriction)) {
            return `array(${actualRestriction.length})`
          }
          if (actualRestriction && typeof actualRestriction === 'object' && typeof actualRestriction.match === 'function') {
            return `match:${actualRestriction.match.toString()}`
          }
          return String(actualRestriction)
        } catch (error) {
          return `unavailable:${error instanceof Error ? error.message : 'error'}`
        }
      })()
      serverEventRegistry.register(formId, predicate, (event) => {
        scheduleCoalescedServerEventListenerRun(
          `${formId}:${source ?? 'listener'}`,
          event,
          160,
          async (latestEvent) => {
            const enInstance = getEn()
            const proxyEvent = createProxyEvent(latestEvent)
            const eventCoreId =
              latestEvent.coreId != null && String(latestEvent.coreId).trim() !== ''
                ? String(latestEvent.coreId).trim()
                : null
            if (typeof enInstance?._setServerEventCoreId === 'function') {
              enInstance._setServerEventCoreId(eventCoreId)
            }
            try {
              if (typeof onBeforeListener === 'function') {
                await Promise.resolve(onBeforeListener())
              }
              // Дать chainCalled / merge sessionId и rawDraft на core завершиться до reloadPanel.
              await new Promise((resolve) => setTimeout(resolve, 400))
              await withEventData(
                enInstance,
                latestEvent?.data as Record<string, any> | undefined,
                async () => {
                  await Promise.resolve(actualCallback(enInstance, proxyEvent))
                  if (typeof onAfterListener === 'function') {
                    onAfterListener(enInstance)
                  }
                },
              )
            } catch (error) {
              console.error('[ServerEvents] Error in server event listener:', error, { source })
            } finally {
              if (typeof enInstance?._setServerEventCoreId === 'function') {
                enInstance._setServerEventCoreId(null)
              }
            }
          },
        )
      }, source, registeredSessionId)
    },
    clear: () => serverEventRegistry.clearForm(formId)
  }
}
